import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import FormDots from '../common/FormDots';
import { Button, Col, Input, InputNumber, Row } from 'antd';
import { Form } from '@ant-design/compatible';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { addItem, getFixedFloatSum, removeItem } from '../../utils/InvoiceUtils';

const EquipmentFormFields: FC<any> = ({
	formData,
	form,
	currency,
	initialUuid,
	state,
	setState,
	recalculateTotals,
}): React.ReactElement => {
	const [, setUuid] = useState(initialUuid);

	const { getFieldDecorator, getFieldValue, setFieldsValue } = form;

	const equipmentKeys = getFieldValue('equipmentKeys') || [];

	const handleEquipmentChange = useCallback(
		(val, k) => {
			const equipmentCharge = val;
			const equipmentKeys = getFieldValue(`equipmentKeys`);
			if (equipmentCharge) {
				setFieldsValue({ [`equipment-${k}-amount`]: equipmentCharge.toString() });
			} else {
				setFieldsValue({ [`equipment-${k}-amount`]: undefined });
			}
			const equipmentTotalBeforeTax = equipmentKeys.reduce((acc, k2) => {
				const price =
					k2 === k ? equipmentCharge : getFieldValue(`equipment-${k2}-unitPrice`) || '0';
				return getFixedFloatSum(acc, price);
			}, 0);

			setFieldsValue({ equipmentTotalBeforeTax: equipmentTotalBeforeTax });
			recalculateTotals();
		},
		[getFieldValue, recalculateTotals, setFieldsValue]
	);

	const equipmentFormItems = useMemo(
		() =>
			equipmentKeys.map((k, equipmentIndex) => {
				getFieldDecorator(`equipment-${k}-equipmentId`, {
					initialValue: nullSafeGet(`equipment-${k}-equipmentId`, formData),
				});
				return (
					<Row key={k}>
						<Col span={1}>
							<div
								style={
									equipmentIndex === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
										  }
								}
							>
								{equipmentIndex + 1}
							</div>
						</Col>

						<Col span={7}>
							<Form.Item label={equipmentIndex === 0 ? 'Description' : ''}>
								{getFieldDecorator(`equipment-${k}-description`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`equipment-${k}-description`, formData) ||
										nullSafeGet(`equipmentItemInitialValues.equipment-${k}-description`, state),
									rules: [
										{
											required: true,
											whitespace: true,
											message: 'Please add description.',
										},
									],
								})(<Input />)}
							</Form.Item>
						</Col>
						<Col span={4} offset={1}>
							<Form.Item label={equipmentIndex === 0 ? 'Equipment Number' : ''}>
								{getFieldDecorator(`equipment-${k}-equipmentNumber`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`equipment-${k}-equipmentNumber`, formData) ||
										nullSafeGet(`equipmentItemInitialValues.equipment-${k}-equipmentNumber`, state)
											? (
													nullSafeGet(`equipment-${k}-equipmentNumber`, formData) ||
													nullSafeGet(
														`equipmentItemInitialValues.equipment-${k}-equipmentNumber`,
														state
													)
											  ).toString()
											: undefined,
								})(<Input />)}
							</Form.Item>
						</Col>

						<Col span={3} offset={1}>
							<Form.Item label={equipmentIndex === 0 ? 'Price' : ''}>
								{getFieldDecorator(`equipment-${k}-unitPrice`, {
									validateTrigger: ['onBlur'],
									initialValue:
										nullSafeGet(`equipment-${k}-unitPrice`, formData) ||
										nullSafeGet(`equipmentItemInitialValues.equipment-${k}-unitPrice`, state),
									rules: [
										{
											required: true,
											message: 'Please add price.',
										},
									],
								})(
									<InputNumber
										step={0.01}
										onChange={(e) => handleEquipmentChange(e, k)}
										style={{ width: '100%' }}
										addonAfter={currency.id}
									/>
								)}
							</Form.Item>
						</Col>
						<Col
							span={1}
							style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}
						>
							<div
								style={
									equipmentIndex === 0
										? {
												height: 40,
												display: 'flex',
												justifyContent: 'center',
												alignItems: 'center',
												marginTop: 30,
										  }
										: {
												height: 40,
												display: 'flex',
												alignItems: 'center',
										  }
								}
							>
								{equipmentKeys.length > 0 ? (
									<MinusCircleOutlined
										className="dynamic-delete-button"
										onClick={() =>
											removeItem(form, recalculateTotals)('equipmentKeys', 'equipment', k)
										}
									/>
								) : null}
							</div>
						</Col>

						<Col span={2} offset={4}>
							<div
								style={
									equipmentIndex === 0
										? {
												height: 40,
												marginTop: 29,
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
										  }
										: {
												height: 40,
												display: 'flex',
												justifyContent: 'flex-end',
												alignItems: 'center',
										  }
								}
							>
								{getFieldDecorator(`equipment-${k}-amount`, {
									initialValue:
										formData[`equipment-${k}-amount`] ||
										nullSafeGet(`equipmentItemInitialValues.equipment-${k}-amount`, state),
								})}
								{currency.format(getFieldValue(`equipment-${k}-amount`))}
							</div>
						</Col>
					</Row>
				);
			}),
		[
			currency,
			equipmentKeys,
			form,
			formData,
			getFieldDecorator,
			getFieldValue,
			handleEquipmentChange,
			recalculateTotals,
			state,
		]
	);

	const onAddItem = useCallback(() => {
		setUuid((uuid) =>
			addItem(form, state, setState)('equipmentKeys', uuid, 'equipmentTotalBeforeTax')
		);
	}, [form, setState, state]);

	return (
		<div style={{ marginBottom: 32 }}>
			<div className="buyerInvoiceForm__sectionHead">
				<div className="buyerInvoiceForm__sectionHeadPrimary">Equipment</div>
				<FormDots />
				{nullSafeGetOrElse('length', equipmentKeys, 0) > 0 ? (
					getFieldDecorator(`equipmentTotalBeforeTax`, {
						validateTrigger: ['onBlur'],
						initialValue: `${nullSafeGetOrElse(`equipmentTotalBeforeTax`, formData, 0)}`,
					})(
						<div className="buyerInvoiceForm__sectionHeadSecondary">
							{currency.format(getFieldValue(`equipmentTotalBeforeTax`))}
						</div>
					)
				) : (
					<div className="buyerInvoiceForm__sectionHeadSecondary">
						{getFieldDecorator(`equipmentTotalBeforeTax`, {
							validateTrigger: ['onBlur'],
							initialValue: `${nullSafeGetOrElse(`equipmentTotalBeforeTax`, formData, 0)}`,
						})(
							<InputNumber
								step={1}
								onChange={recalculateTotals}
								style={{ width: 240 }}
								addonAfter={currency.id}
							/>
						)}
					</div>
				)}
			</div>
			{equipmentFormItems}
			<Row>
				<Col span={16} offset={1}>
					<Button type="dashed" onClick={onAddItem} style={{ height: 32 }}>
						<PlusOutlined /> Add equipment
					</Button>
				</Col>
			</Row>
		</div>
	);
};

export default EquipmentFormFields;
